/**
 * 前端埋点 SDK（生命周期"数据分析"PV/UV，ARCHITECTURE_DESIGN §5.6）。
 *
 * - session uuid：首次访问生成并存入 localStorage（`dp_analytics_session_id`），
 *   后端以 distinct session_id 计算 UV；
 * - 页面访问（track）：节流上报（500ms 窗口，避免路由抖动重复上报）；
 * - 停留时长：beforeunload / visibilitychange(hidden) 时用 sendBeacon
 *   （或 fetch keepalive）上报 duration_seconds；
 * - 汇总查询：getAppAnalyticsSummary 供 dashboard 数据分析卡片使用。
 */

import { API_BASE_URL, apiClient } from "@/services/api";

const SESSION_KEY = "dp_analytics_session_id";
const TRACK_THROTTLE_MS = 500;

const TRACK_URL = `${API_BASE_URL}/analytics/track`;

let sessionId: string | null = null;
let lastTrackAt = 0;
let pageStartedAt = 0;
let activeAppId: string | null = null;
let activePath: string | null = null;
let teardown: (() => void) | null = null;

export interface AppAnalyticsSummary {
  app_id: string;
  days: number;
  total_pv: number;
  total_uv: number;
  avg_duration_seconds: number;
  daily: { date: string; pv: number; uv: number }[];
  top_paths: { path: string; pv: number }[];
}

/** 读取/生成会话 uuid（localStorage 持久化，刷新不换、实现 UV 去重）。 */
export function getSessionId(): string {
  if (sessionId) return sessionId;
  try {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing) {
      sessionId = existing;
      return sessionId;
    }
    const generated = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, generated);
    sessionId = generated;
  } catch {
    sessionId = sessionId ?? `anon-${Math.random().toString(36).slice(2)}`;
  }
  return sessionId;
}

function postTrack(payload: Record<string, unknown>): void {
  const body = JSON.stringify(payload);
  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    const blob = new Blob([body], { type: "application/json" });
    void navigator.sendBeacon(TRACK_URL, blob);
    return;
  }
  fetch(TRACK_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {
    /* 埋点失败静默：不阻塞业务 */
  });
}

/** 上报一次页面访问（节流）。 */
export function trackPageView(appId: string, path: string): void {
  const now = Date.now();
  if (now - lastTrackAt < TRACK_THROTTLE_MS) return;
  lastTrackAt = now;
  postTrack({
    app_id: appId,
    session_id: getSessionId(),
    path,
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
  });
}

function flushDuration(): void {
  if (!activeAppId || !activePath || pageStartedAt === 0) return;
  const durationSeconds = (Date.now() - pageStartedAt) / 1000;
  pageStartedAt = 0;
  if (durationSeconds < 1) return;
  postTrack({
    app_id: activeAppId,
    session_id: getSessionId(),
    path: activePath,
    duration_seconds: Math.round(durationSeconds * 100) / 100,
  });
}

/**
 * 初始化埋点：上报页面访问并开始停留计时；页面切换时先结算上一页的停留时长。
 * 同一时刻只保留一组监听（重复调用会先拆除旧监听）。
 */
export function initAnalytics(appId: string, path: string): void {
  flushDuration();
  activeAppId = appId;
  activePath = path;
  pageStartedAt = Date.now();
  trackPageView(appId, path);

  teardown?.();
  const onLeave = () => flushDuration();
  const onVisibility = () => {
    if (document.visibilityState === "hidden") flushDuration();
  };
  window.addEventListener("beforeunload", onLeave);
  document.addEventListener("visibilitychange", onVisibility);
  teardown = () => {
    window.removeEventListener("beforeunload", onLeave);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}

/** 测试辅助：清空模块级状态与事件监听。 */
export function resetAnalyticsState(): void {
  teardown?.();
  teardown = null;
  sessionId = null;
  lastTrackAt = 0;
  pageStartedAt = 0;
  activeAppId = null;
  activePath = null;
}

/** 拉取应用 PV/UV 汇总（dashboard 数据分析卡片；API 不可用时返回 null）。 */
export async function getAppAnalyticsSummary(
  appId: string,
  days = 7,
): Promise<AppAnalyticsSummary | null> {
  try {
    const response = await apiClient.get(
      `/analytics/apps/${encodeURIComponent(appId)}/summary`,
      { params: { days } },
    );
    return response.data as AppAnalyticsSummary;
  } catch {
    return null;
  }
}
